import { useCallback, useEffect, useState } from "react"
import api from "../lib/api"

export interface GalleryMember {
  id: string
  firstName: string | null
  lastName: string | null
  // Role for THIS team/year (CccdTeamMember.role) — re-selected every
  // season, never a permanent attribute of the student's own profile.
  role: "student" | "tutor" | "unassigned"
  photoUrl: string | null
}

export interface GalleryTeam {
  id: string
  name: string
  categorySlug: string | null
  institutionName: string | null
  institutionCity: string | null
  institutionCountry: string | null
  motor: string | null
  scope: "national" | "regional"
  // The patch is the team's emblem/logo; the group photo is an actual
  // picture of every member together — distinct concepts, both admin-managed.
  patchImageUrl: string | null
  groupPhotoUrl: string | null
  members: GalleryMember[]
}

/** Team showcase gallery for a given CCCD event — see
 * openspec/changes/academy-staff-unrestricted-access (event-scoped, was
 * category-scoped in cccd-category-scoped-contest-info). Read-only: team
 * patches and member photos are managed by admins from gestion, not
 * uploaded by students from Academy. */
export function useCccdTeamGallery(eventId: string | null) {
  const [teams, setTeams] = useState<GalleryTeam[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refetch = useCallback(() => {
    if (!eventId) {
      setTeams([])
      setIsLoading(false)
      return
    }
    setIsLoading(true)
    setError(null)
    api
      .get("/cccd/me/teams", { params: { eventId } })
      .then(({ data }) => setTeams(data.data ?? []))
      .catch(() => setError("No se pudo cargar la galería de equipos."))
      .finally(() => setIsLoading(false))
  }, [eventId])

  useEffect(() => {
    refetch()
  }, [refetch])

  return { teams, isLoading, error, refetch }
}
